import * as React from 'react';
import { useMemo } from 'react';
import i18next from 'i18next';
import { Bar, HorizontalBar } from 'react-chartjs-2';
import { Bucket, Metric, axisValue, bucketLabel, formatMetric, metricValue, wrapLabel } from '../../items';
import styles from './styles.module.scss';

interface Props {
  buckets: Bucket[];
  metric: Metric;
  orientation: 'vertical' | 'horizontal';
  /** Label of the bucket without a value (e.g. "Sin tipo"). */
  emptyLabel: string;
  colorOf: (bucket: Bucket, index: number) => string;
  /** Called with a clicked bar's bucket; the bars aren't clickable without it. */
  onSelect?: (bucket: Bucket) => void;
}

const FONT = "'Work Sans', Arial, sans-serif";
const TEXT_COLOR = '#3E3E3E';
const WRAP_AT = 22;
const VALUE_FONT = `600 11px ${FONT}`;
const VALUE_GAP = 6;

interface BarElement {
  _model: { x: number; y: number };
}

interface ChartInstance {
  ctx: CanvasRenderingContext2D;
  config: { type: string };
  data: { datasets: { valueLabels?: string[]; valueColors?: string[] }[] };
  getDatasetMeta: (index: number) => { data: BarElement[] };
}

let measureContext: CanvasRenderingContext2D | null = null;

/** Width in px of the widest value, as the plugin draws it. */
const widestValue = (labels: string[]): number => {
  measureContext = measureContext || document.createElement('canvas').getContext('2d');
  if (!measureContext) {
    return 0;
  }
  measureContext.font = VALUE_FONT;
  return Math.max(0, ...labels.map(label => measureContext!.measureText(label).width));
};

// Each bar's value at its end, in the bar's color. The labels travel in the
// dataset (react-chartjs-2 keeps the plugins it was created with, but copies the dataset's properties
// on every update).
const VALUE_LABELS_PLUGIN = {
  afterDatasetsDraw: (chart: ChartInstance) => {
    const { valueLabels = [], valueColors = [] } = chart.data.datasets[0] || {};
    const horizontal = chart.config.type === 'horizontalBar';
    const { ctx } = chart;
    ctx.save();
    ctx.font = VALUE_FONT;
    ctx.textAlign = horizontal ? 'left' : 'center';
    ctx.textBaseline = horizontal ? 'middle' : 'bottom';
    chart.getDatasetMeta(0).data.forEach((bar, index) => {
      const { x, y } = bar._model;
      ctx.fillStyle = valueColors[index] || TEXT_COLOR;
      ctx.fillText(valueLabels[index] ?? '', horizontal ? x + VALUE_GAP : x, horizontal ? y : y - 4);
    });
    ctx.restore();
  }
};

/** A bar per bucket of the chosen metric: Acciones, or investment in USD millions. */
const MetricBarChart = ({ buckets, metric, orientation, emptyLabel, colorOf, onSelect }: Props) => {
  const horizontal = orientation === 'horizontal';
  const unit = i18next.t(metric === 'count' ? 'analytics:unitCount' : 'analytics:unitInvestment') as string;

  const { data, options } = useMemo(() => {
    const values = buckets.map(bucket => metricValue(bucket, metric));
    // Always set: Chart.js 2 merges new options into the old ones, so a callback that is only set for
    // the investment would stay on the axis after switching back to the count.
    // Horizontal charts measure along X: the axis and its title go on top, where the chart starts.
    const valueAxis = {
      ...(horizontal && {
        position: 'top',
        scaleLabel: { display: true, labelString: unit, fontFamily: FONT, fontSize: 11, fontColor: TEXT_COLOR }
      }),
      ticks: { min: 0, fontFamily: FONT, callback: (v: number) => axisValue(v, metric) }
    };
    const labelAxis = { gridLines: { display: false }, ticks: { fontFamily: FONT, fontSize: 11 } };
    const valueLabels = values.map(value => formatMetric(value, metric));
    const colors = buckets.map(colorOf);
    // Just the room the values need past the longest bar (or above the tallest), so the bars get the rest.
    const padding = horizontal ? { right: Math.ceil(widestValue(valueLabels)) + VALUE_GAP + 2 } : { top: 20 };
    return {
      data: {
        // Long names break into lines (an array label) so a horizontal chart doesn't cut them.
        labels: buckets.map(bucket => {
          const label = bucketLabel(bucket, emptyLabel);
          return horizontal ? wrapLabel(label, WRAP_AT) : label;
        }),
        datasets: [{
          data: values,
          valueLabels,
          valueColors: colors,
          backgroundColor: colors,
          barPercentage: 0.9,
          categoryPercentage: 0.9
        }]
      },
      options: {
        maintainAspectRatio: false,
        layout: { padding },
        legend: { display: false },
        tooltips: { callbacks: { label: (item: { index: number }) => formatMetric(values[item.index], metric) } },
        scales: horizontal ? { xAxes: [valueAxis], yAxes: [labelAxis] } : { xAxes: [labelAxis], yAxes: [valueAxis] },
        ...(onSelect && {
          onClick: (_: unknown, elements: { _index: number }[]) => {
            const bucket = elements[0] && buckets[elements[0]._index];
            if (bucket && bucket.id !== null) {
              onSelect(bucket);
            }
          }
        })
      }
    };
  }, [buckets, metric, horizontal, orientation, unit, emptyLabel, colorOf, onSelect]);

  const Chart = horizontal ? HorizontalBar : Bar;
  return (
    <div>
      {!horizontal && <p className={styles.unit}>{unit}</p>}
      <div style={{ height: horizontal ? Math.max(240, buckets.length * 38) : 280 }}>
        <Chart data={data} options={options} plugins={[VALUE_LABELS_PLUGIN]} />
      </div>
    </div>
  );
};

export default MetricBarChart;
